import React, { useState, useEffect, useRef } from "react";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonModal,
    IonContent,
} from "@ionic/react";
import { Helmet } from "react-helmet";

// Define question types
type QuestionType = "picture" | "identifyOddOneOut";

interface PictureQuestion {
    question: string;
    options: { src: string; answer: boolean }[];
}

interface IdentifyOddOneOutQuestion {
    question: string;
    options: string[];
    correct: string;
}


const LivingNonLivingWorksheet: React.FC = () => {
    const [currentQuestion, setCurrentQuestion] = useState<PictureQuestion | IdentifyOddOneOutQuestion | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | boolean | null>(null);
    const [showResult, setShowResult] = useState<boolean>(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const [questionBank, setQuestionBank] = useState<Record<QuestionType, (PictureQuestion | IdentifyOddOneOutQuestion)[]>>({
        picture: [],
        identifyOddOneOut: []
    });

    const fetchQuestions = async () => {
        try {
            let identifyOddOneOutQuestion: IdentifyOddOneOutQuestion[] = [];
            let pictureQuestionQuestion: PictureQuestion[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {

                const response2 = await fetch(process.env.REACT_APP_API_URL + 'LivingNonLivingIdentifyOddOneOut.json');
                identifyOddOneOutQuestion = await response2.json();

                const response4 = await fetch(process.env.REACT_APP_API_URL + 'LivingNonLivingPictureQuestion.json');
                pictureQuestionQuestion = await response4.json();

            } else {

                const module2 = await import('../../../datas/LivingNonLivingIdentifyOddOneOut.json');
                identifyOddOneOutQuestion = module2.default;

                const module4 = await import('../../../datas/LivingNonLivingPictureQuestion.json');
                pictureQuestionQuestion = module4.default;
            }
            setQuestionBank({
                picture: pictureQuestionQuestion,
                identifyOddOneOut: identifyOddOneOutQuestion
            });
        } catch (error) {
            console.error("Error loading questions:", error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Living and Non-Living Things Worksheet for LKG | Environmental Science"; // Set page title
    }, []);

    useEffect(() => {
        if (questionBank.identifyOddOneOut.length) {
            getRandomQuestion();
        }
    }, [questionBank]);

    const getRandomQuestion = () => {
        const questionTypes = Object.keys(questionBank) as QuestionType[];
        const randomType = questionTypes[Math.floor(Math.random() * questionTypes.length)];
        const randomQuestion = questionBank[randomType][Math.floor(Math.random() * questionBank[randomType].length)];
        setCurrentQuestion(randomQuestion);
        setSelectedOption(null);
    };

    const handleAnswerSubmit = (option: any) => {
        setSelectedOption(option);
        if (!currentQuestion) return;

        if ("correct" in currentQuestion) {
            if (option === currentQuestion.correct) {
                showNextQuestion();
                setIsCorrect(true);
            }
        } else if ("options" in currentQuestion) {
            // For picture-based questions
            if ((currentQuestion as PictureQuestion).options.find((opt) => opt.src === option)?.answer) {
                showNextQuestion();
                setIsCorrect(true);
            }
        }
    };

    const showNextQuestion = () => {
        setShowImage(true);
        setTimeout(() => {
            setShowImage(false);
            setSelectedOption(null);
            getRandomQuestion();
            setIsCorrect(false)
        }, 5000);
    }

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach your LKG child the difference between living and non-living things with easy examples and fun worksheets. Help them identify what grows, eats, and moves through engaging activities and real-life visuals." />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Living and Non-Living Things</h1>

                                <p>
                                    Understanding the difference between <strong>living and non-living things</strong> is a foundational topic in early science education. This LKG-level worksheet introduces children to the characteristics of living beings — things that <strong>eat, breathe, grow, move, and reproduce</strong> — and helps them identify what is not alive, such as toys, books, or rocks.
                                </p>

                                <h2>🌱 What Are Living Things?</h2>
                                <p>
                                    Living things are those that are <strong>alive</strong>. They grow, move, breathe, need food and water, and can have babies. Examples include:
                                </p>
                                <ul>
                                    <li>Humans</li>
                                    <li>Animals</li>
                                    <li>Plants</li>
                                </ul>

                                <h2>🪨 What Are Non-Living Things?</h2>
                                <p>
                                    Non-living things do <strong>not grow or need food</strong>. They cannot move by themselves. Examples include:
                                </p>
                                <ul>
                                    <li>Chair</li>
                                    <li>Ball</li>
                                    <li>Car</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify and classify objects as living or non-living</li>
                                    <li>Understand basic characteristics of life (<strong>growth, movement, food, etc.</strong>)</li>
                                    <li>Enhance observation and reasoning skills</li>
                                    <li>Improve vocabulary and general awareness</li>
                                </ul>

                                <h2>📋 What’s Inside the Worksheet?</h2>
                                <ul>
                                    <li>Circle the living things in a picture</li>
                                    <li>Match objects to <strong>“living” or “non-living”</strong> categories</li>
                                    <li>Tick ✓ the living and cross ✗ the non-living items</li>
                                    <li>Color activities to highlight understanding</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents and Teachers</h2>
                                <ul>
                                    <li>Go on a nature walk and ask your child to point out living and non-living things</li>
                                    <li>Use toys and real objects to sort into two groups</li>
                                    <li>Ask questions like <strong>“Can it move on its own?”</strong> or <strong>“Does it grow?”</strong></li>
                                    <li>Watch age-appropriate videos or rhymes about nature and animals</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/environmental-science/animal"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Animal</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/environmental-science/traffic-rules-safety"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Traffic Rules & Safety</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Encourage your child to look around the house and ask questions — <strong>“Is this alive?”</strong> Curiosity leads to understanding!
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Living and Non-Living Things</h1>

                            <p>
                                Understanding the difference between <strong>living and non-living things</strong> is a foundational topic in early science education. This LKG-level worksheet introduces children to the characteristics of living beings — things that <strong>eat, breathe, grow, move, and reproduce</strong> — and helps them identify what is not alive, such as toys, books, or rocks.
                            </p>

                            <h2>🌱 What Are Living Things?</h2>
                            <p>
                                Living things are those that are <strong>alive</strong>. They grow, move, breathe, need food and water, and can have babies. Examples include:
                            </p>
                            <ul>
                                <li>Humans</li>
                                <li>Animals</li>
                                <li>Plants</li>
                            </ul>

                            <h2>🪨 What Are Non-Living Things?</h2>
                            <p>
                                Non-living things do <strong>not grow or need food</strong>. They cannot move by themselves. Examples include:
                            </p>
                            <ul>
                                <li>Chair</li>
                                <li>Ball</li>
                                <li>Car</li>
                            </ul>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify and classify objects as living or non-living</li>
                                <li>Understand basic characteristics of life (<strong>growth, movement, food, etc.</strong>)</li>
                                <li>Enhance observation and reasoning skills</li>
                                <li>Improve vocabulary and general awareness</li>
                            </ul>

                            <h2>📋 What’s Inside the Worksheet?</h2>
                            <ul>
                                <li>Circle the living things in a picture</li>
                                <li>Match objects to <strong>“living” or “non-living”</strong> categories</li>
                                <li>Tick ✓ the living and cross ✗ the non-living items</li>
                                <li>Color activities to highlight understanding</li>
                            </ul>

                            <h2>👩‍🏫 Tips for Parents and Teachers</h2>
                            <ul>
                                <li>Go on a nature walk and ask your child to point out living and non-living things</li>
                                <li>Use toys and real objects to sort into two groups</li>
                                <li>Ask questions like <strong>“Can it move on its own?”</strong> or <strong>“Does it grow?”</strong></li>
                                <li>Watch age-appropriate videos or rhymes about nature and animals</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/lkg/environmental-science/animal"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Animal</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/environmental-science/traffic-rules-safety"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Traffic Rules & Safety</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Encourage your child to look around the house and ask questions — <strong>“Is this alive?”</strong> Curiosity leads to understanding!
                            </p>
                        </article>
                        {currentQuestion ? (
                            <>
                                <h3 className="mb-3">{currentQuestion.question}</h3>

                                {"options" in currentQuestion ? (
                                    // Handle multiple-choice or identify odd one out question
                                    typeof currentQuestion.options[0] != "object" ? (
                                        <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value)}>
                                            {(currentQuestion as IdentifyOddOneOutQuestion).options.map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? isCorrect === true
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}>
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonRadioGroup>
                                    ) : (
                                        // Handle picture question
                                        <div className="row">
                                            {(currentQuestion as PictureQuestion).options.map((option, index) => (
                                                <div
                                                    className={`col-6 col-md-3 mb-5 `}
                                                    key={index}>
                                                    <img
                                                        src={process.env.REACT_APP_API_URL + option.src}
                                                        onClick={() => handleAnswerSubmit(option.src)}
                                                        className={`livingImage img-thumbnail ${selectedOption === option.src
                                                            ? isCorrect === true
                                                                ? "correct"
                                                                : "errorImgItems"
                                                            : ""
                                                            }`}
                                                        style={{
                                                            cursor: "pointer",
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )
                                ) :
                                    <>
                                        <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value!)}>
                                            <IonItem
                                                className={`grid-item ${selectedOption === true
                                                    ? isCorrect === true
                                                        ? "correct"
                                                        : "errorItems"
                                                    : ""
                                                    }`}>
                                                <IonRadio slot="start" value={true} />
                                                <IonLabel>True</IonLabel>
                                            </IonItem>
                                            <IonItem
                                                className={`grid-item ${selectedOption === false
                                                    ? isCorrect === true
                                                        ? "correct"
                                                        : "errorItems"
                                                    : ""
                                                    }`}>
                                                <IonRadio slot="start" value={false} />
                                                <IonLabel>False</IonLabel>
                                            </IonItem>
                                        </IonRadioGroup>
                                    </>
                                }
                            </>
                        ) : (
                            <p>Loading question...</p>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default LivingNonLivingWorksheet;